import { useEffect, useMemo, useState } from "react";
import { Spin } from "antd";
import { Trans } from "react-i18next";
import classNames from "classnames";
import {
  PathsApiUserAssetsGetParametersQueryAssetSource,
  PathsApiUserAssetsGetParametersQueryAssetType,
  UserCollectionRequestType,
} from "@apis/generated/openapi";
import { fetchCollectionList, getUserAssetsV2, type UserAssetV2Record } from "@apis/http/api";
import { Tabs, type TabOption } from "@src/ui/Tabs";
import Image from "@components/Image";
import { getAssetCoverUrl } from "@pages/library/hooks/useGeneratedVideoAssets";

type VideoSource = "generated" | "uploaded" | "saved";

const TAB_OPTIONS: TabOption[] = [
  { value: "generated", label: "Generated" },
  { value: "uploaded", label: "Uploaded" },
  { value: "saved", label: "Saved" },
];

interface PickableVideo {
  id: string;
  coverImageUrl?: string;
  title?: string;
}

const PAGE_SIZE = 30;

export interface VideoLibraryPickerProps {
  /** 选中视频后由父组件负责把封面写入主图槽并跳到 editor */
  onSelect: (video: PickableVideo) => void;
}

const fetchByTab = async (tab: VideoSource): Promise<PickableVideo[]> => {
  if (tab === "generated") {
    const resp = await getUserAssetsV2({
      assetType: PathsApiUserAssetsGetParametersQueryAssetType.VID,
      assetSource: PathsApiUserAssetsGetParametersQueryAssetSource.AI,
      pageNo: 1,
      pageSize: PAGE_SIZE,
    });
    const list = (resp?.data ?? []).filter((r) => r.state === undefined || r.state === 1);
    return list.map((r: UserAssetV2Record) => ({
      id: String(r.id),
      coverImageUrl: getAssetCoverUrl(r),
      title: r.assetDescription || r.fileName,
    }));
  }
  if (tab === "uploaded") {
    const resp = await getUserAssetsV2({
      assetType: PathsApiUserAssetsGetParametersQueryAssetType.VID,
      assetSource: PathsApiUserAssetsGetParametersQueryAssetSource.USER,
      pageNo: 1,
      pageSize: PAGE_SIZE,
    });
    const list = (resp?.data ?? []).filter((r) => r.state === undefined || r.state === 1);
    return list.map((r: UserAssetV2Record) => ({
      id: String(r.id),
      coverImageUrl: getAssetCoverUrl(r),
      title: r.assetDescription || r.fileName,
    }));
  }
  // saved
  const resp = (await fetchCollectionList({ type: UserCollectionRequestType.Value1 } as any)) as unknown as any[];
  return (resp ?? []).map((r) => ({
    id: String(r.collectionId ?? r.id ?? r.videoId ?? ""),
    coverImageUrl: r.coverImageUrl,
    title: r.description ?? r.title ?? "",
  }));
};

/**
 * Create Character Modal 「从视频库选」入口。
 *
 * 设计稿节点：3536:23405 - 顶部 Generated / Uploaded / Saved 三 Tab，
 * 下方网格列出可选视频；点选某条视频后，由上层把视频封面 URL 转 File 写到主图槽位。
 */
export const VideoLibraryPicker = ({ onSelect }: VideoLibraryPickerProps) => {
  const [tab, setTab] = useState<VideoSource>("generated");
  const [list, setList] = useState<PickableVideo[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setSelectedId(null);
    fetchByTab(tab)
      .then((items) => {
        if (!cancelled) setList(items);
      })
      .catch(() => {
        if (!cancelled) setList([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [tab]);

  const records = useMemo(() => list.filter((r) => !!r.coverImageUrl), [list]);

  return (
    <div className="pt-[8px] flex flex-col gap-[16px]">
      <div className="border-b border-kds-border">
        <Tabs type="text" value={tab} options={TAB_OPTIONS} onChange={(value) => setTab(value as VideoSource)} />
      </div>
      <Spin spinning={loading}>
        <div className="max-h-[60vh] min-h-[300px] overflow-y-auto kds-scroll-thin -mr-[8px] pr-[8px]">
          {records.length === 0 && !loading ? (
            <div className="flex items-center justify-center h-[300px] text-kds-fg-soft text-[14px]">
              <Trans>Nothing here yet.</Trans>
            </div>
          ) : (
            <div className="grid grid-cols-4 gap-[12px]">
              {records.map((record) => {
                const isSelected = selectedId === record.id;
                return (
                  <div key={record.id} className="relative">
                    <div
                      className="cursor-pointer bg-kds-bg-card border border-kds-border rounded-[8px] overflow-hidden hover:border-kds-accent transition-colors"
                      onClick={() => {
                        setSelectedId(record.id);
                        onSelect(record);
                      }}
                    >
                      <Image
                        url={record.coverImageUrl}
                        className="w-full aspect-3/4 object-cover border-none! rounded-none!"
                      />
                      {record.title && (
                        <div className="px-[8px] py-[10px]">
                          <p className="text-[13px] text-kds-fg leading-[18px] line-clamp-2 m-0">{record.title}</p>
                        </div>
                      )}
                    </div>
                    <div
                      className={classNames(
                        "absolute z-10 -top-[2px] -right-[2px] -bottom-[2px] -left-[2px] border-2 rounded-[10px] pointer-events-none transition-colors",
                        isSelected ? "border-kds-accent!" : "border-transparent",
                      )}
                    />
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </Spin>
    </div>
  );
};
